import { useEffect, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import {
  fetchConsignmentDetail,
  addConsignmentPayment,
  deleteConsignmentPayment,
  updateConsignmentItem,
  type ConsignmentDetail,
} from '@/services/consignments';

interface ConsignmentDetailModalProps {
  consignmentId: string;
  onClose: () => void;
  onSaved: () => void;
}

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  pendiente: { label: 'Pendiente', color: 'text-amber-700', bg: 'bg-amber-50' },
  parcial: { label: 'Pago parcial', color: 'text-accent-700', bg: 'bg-accent-50' },
  pagado: { label: 'Pagado total', color: 'text-emerald-700', bg: 'bg-emerald-50' },
};

export default function ConsignmentDetailModal({ consignmentId, onClose, onSaved }: ConsignmentDetailModalProps) {
  const [detail, setDetail] = useState<ConsignmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    const d = await fetchConsignmentDetail(consignmentId);
    setDetail(d);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consignmentId]);

  const totalToPay =
    detail?.items.reduce(
      (s, it) => s + Math.max(0, it.quantityDelivered - it.quantityReturned) * it.unitPrice,
      0,
    ) ?? 0;
  const paid = detail?.payments.reduce((s, p) => s + p.amount, 0) ?? 0;
  const remaining = Math.max(0, totalToPay - paid);
  const status = paid <= 0 ? 'pendiente' : paid >= totalToPay ? 'pagado' : 'parcial';
  const meta = STATUS_META[status];

  async function handleAddPayment(e: FormEvent) {
    e.preventDefault();
    setError('');
    const amt = Number(amount) || 0;
    if (amt <= 0) {
      setError('El monto debe ser mayor a cero.');
      return;
    }
    setSaving(true);
    const { error: addError } = await addConsignmentPayment({
      consignmentId,
      amount: amt,
      paidAt: paidAt || '',
      notes,
    });
    setSaving(false);
    if (addError) {
      setError(addError);
      return;
    }
    setAmount('');
    setNotes('');
    await load();
    onSaved();
  }

  async function handlePayTotal() {
    setError('');
    if (remaining <= 0) return;
    setSaving(true);
    const { error: addError } = await addConsignmentPayment({
      consignmentId,
      amount: remaining,
      paidAt: paidAt || '',
      notes: notes.trim() || 'Pago total',
    });
    setSaving(false);
    if (addError) {
      setError(addError);
      return;
    }
    setNotes('');
    await load();
    onSaved();
  }

  async function handleDeletePayment(id: string) {
    const { error: delError } = await deleteConsignmentPayment(id, consignmentId);
    if (!delError) {
      await load();
      onSaved();
    }
  }

  async function handleSaveItem(itemId: string, returned: number, sold: number) {
    const { error: updError } = await updateConsignmentItem(itemId, consignmentId, {
      quantityReturned: returned,
      quantitySold: sold,
    });
    if (updError) {
      setError(updError);
      return;
    }
    await load();
    onSaved();
  }

  function formatDate(value: string) {
    if (!value) return '—';
    return new Date(value + 'T00:00:00').toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  return (
    <Modal
      title="Factura de consignación"
      subtitle={detail ? detail.distributorName : 'Cargando...'}
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      {loading ? (
        <div className="text-center py-12 text-foreground-400">
          <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>Cargando...
        </div>
      ) : !detail ? (
        <div className="text-center py-12 text-foreground-500">No se pudo cargar la consignación.</div>
      ) : (
        <div className="space-y-5">
          {/* Resumen de pago */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-background-50 rounded-lg p-3 text-center">
              <p className="text-xs text-foreground-500">A pagar</p>
              <p className="text-lg font-bold text-foreground-950 mt-0.5">${totalToPay.toLocaleString()}</p>
            </div>
            <div className="bg-background-50 rounded-lg p-3 text-center">
              <p className="text-xs text-foreground-500">Abonado</p>
              <p className="text-lg font-bold text-emerald-600 mt-0.5">${paid.toLocaleString()}</p>
            </div>
            <div className="bg-background-50 rounded-lg p-3 text-center">
              <p className="text-xs text-foreground-500">Saldo</p>
              <p className="text-lg font-bold text-amber-600 mt-0.5">${remaining.toLocaleString()}</p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${meta.color} ${meta.bg}`}>
              {meta.label}
            </span>
            {remaining > 0 && (
              <button
                type="button"
                onClick={handlePayTotal}
                disabled={saving}
                className="text-xs px-3 py-1.5 bg-accent-500 text-white rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                <i className="ri-check-double-line mr-1"></i>Marcar pago total
              </button>
            )}
          </div>

          {/* Items */}
          <div>
            <p className="text-xs font-semibold text-foreground-700 mb-2">Mercancía entregada</p>
            <div className="space-y-2">
              {detail.items.length === 0 ? (
                <p className="text-sm text-foreground-400">Sin ítems registrados.</p>
              ) : (
                detail.items.map((it) => (
                  <ItemRow key={it.id} item={it} onSave={handleSaveItem} />
                ))
              )}
            </div>
          </div>

          {/* Abonos */}
          <div>
            <p className="text-xs font-semibold text-foreground-700 mb-2">Abonos registrados</p>
            {detail.payments.length === 0 ? (
              <p className="text-sm text-foreground-400">Sin abonos registrados.</p>
            ) : (
              <div className="space-y-2 max-h-[180px] overflow-y-auto">
                {detail.payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 bg-white border border-background-200 rounded-lg px-3 py-2">
                    <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-bank-card-line text-accent-600"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-900">${p.amount.toLocaleString()}</p>
                      <p className="text-xs text-foreground-500 truncate">
                        {formatDate(p.paidAt)}
                        {p.notes ? ` · ${p.notes}` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeletePayment(p.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer"
                      aria-label="Eliminar abono"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form de abono */}
          <form onSubmit={handleAddPayment} className="space-y-3 bg-background-50 border border-background-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-foreground-700">Registrar abono</p>
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line"></i>
                <span>{error}</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Monto ($) *</label>
                <input type="number" min="0" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Fecha</label>
                <input type="date" className={inputCls} value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Nota</label>
              <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Abono parcial, transferencia..." maxLength={200} />
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={saving} className="text-sm px-4 py-2 bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
                {saving ? 'Guardando...' : 'Agregar abono'}
              </button>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
}

function ItemRow({
  item,
  onSave,
}: {
  item: {
    id: string;
    productName: string;
    sizeLabel: string;
    quantityDelivered: number;
    quantityReturned: number;
    quantitySold: number;
    unitPrice: number;
  };
  onSave: (id: string, returned: number, sold: number) => void;
}) {
  const [returned, setReturned] = useState(String(item.quantityReturned));
  const [sold, setSold] = useState(String(item.quantitySold));
  const subtotal = Math.max(0, item.quantityDelivered - Number(returned || 0)) * item.unitPrice;

  return (
    <div className="border border-background-200 rounded-lg p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground-900">
          {item.productName} {item.sizeLabel && `· ${item.sizeLabel}`}
        </p>
        <span className="text-xs text-foreground-500 whitespace-nowrap">${item.unitPrice.toLocaleString()} c/u</span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
        <div className="text-center bg-background-50 rounded-md py-1.5">
          <p className="text-foreground-500">Entregados</p>
          <p className="font-semibold text-foreground-900">{item.quantityDelivered}</p>
        </div>
        <div className="text-center">
          <p className="text-foreground-500 mb-1">Devueltos</p>
          <input
            type="number"
            min="0"
            max={item.quantityDelivered}
            className="w-full text-center text-sm border border-background-200 rounded-md py-1 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
            value={returned}
            onChange={(e) => setReturned(e.target.value)}
          />
        </div>
        <div className="text-center">
          <p className="text-foreground-500 mb-1">Vendidos</p>
          <input
            type="number"
            min="0"
            className="w-full text-center text-sm border border-background-200 rounded-md py-1 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
            value={sold}
            onChange={(e) => setSold(e.target.value)}
          />
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-foreground-500">
          A pagar: <strong className="text-foreground-900">${subtotal.toLocaleString()}</strong>
        </span>
        <button
          type="button"
          onClick={() => onSave(item.id, Number(returned) || 0, Number(sold) || 0)}
          className="text-xs px-3 py-1.5 bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          Guardar
        </button>
      </div>
    </div>
  );
}